import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  QUOTE_UNIT_SUGGESTIONS,
  blocksToText,
  textToBlocks,
  lineItemAmount,
  quoteTotals,
  type DeliveryMilestone,
  type QuotePlanOption,
  type QuoteTemplate,
  type QuoteTemplateSummary,
  type QuoteUnit,
} from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { PlanEditor } from '../components/QuoteDocumentPanel.tsx';
import { DeliveryPlanEditor } from '../components/DeliveryPlanEditor.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

// Where the templates we sell from are kept.
//
// The content of these is T3's, not the app's: the only one shipped is
// the house SOW skeleton, and everything else is written here. That is
// deliberate - a template is sales copy, it changes, and changing it
// should not need a deploy.
//
// A template is a STARTING POINT. Editing one never reaches back into a
// quote already written from it, which is the first thing anyone wonders
// and is said on screen for that reason.

interface Draft {
  name: string;
  description: string;
  intro: string;
  closing: string;
  // Every field the server keeps is carried, edited or not. This page
  // once posted only heading and text, so saving a copy edit unrolled the
  // agreements and - the route defaulting a missing list to none - wiped
  // the block-hours plans.
  sections: Array<{ heading: string; text: string; collapsible: boolean; showsPricing: boolean }>;
  planOptions: QuotePlanOption[];
  deliveryPlan: DeliveryMilestone[];
  lineItems: Array<{ description: string; quantity: string; unit: QuoteUnit; unitPriceUsd: string }>;
  isActive: boolean;
  position: number;
  depositPercent: number;
}

const inputClass =
  'block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)] outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30';

const money = (value: number): string =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

function emptyDraft(): Draft {
  return {
    name: '',
    description: '',
    intro: '',
    closing: '',
    sections: [{ heading: 'Scope of work', text: '', collapsible: false, showsPricing: false }],
    planOptions: [],
    deliveryPlan: [],
    lineItems: [],
    isActive: true,
    position: 50,
    depositPercent: 50,
  };
}

function draftFrom(template: QuoteTemplate): Draft {
  return {
    name: template.name,
    description: template.description ?? '',
    intro: template.intro ?? '',
    closing: template.closing ?? '',
    sections: template.sections.map((s) => ({
      heading: s.heading,
      text: blocksToText(s.blocks),
      collapsible: s.collapsible ?? false,
      showsPricing: s.showsPricing ?? false,
    })),
    planOptions: template.planOptions ?? [],
    deliveryPlan: template.deliveryPlan ?? [],
    lineItems: template.lineItems.map((i) => ({
      description: i.description,
      quantity: String(i.quantity),
      unit: i.unit,
      unitPriceUsd: String(i.unitPriceUsd),
    })),
    isActive: template.isActive,
    position: template.position,
    depositPercent: template.depositPercent,
  };
}

/** Rows still being typed are strings; only complete ones are sent. */
function pricedRows(
  rows: Draft['lineItems'],
): Array<{ description: string; quantity: number; unit: QuoteUnit; unitPriceUsd: number }> {
  const out: Array<{ description: string; quantity: number; unit: QuoteUnit; unitPriceUsd: number }> = [];
  for (const row of rows) {
    const quantity = Number(row.quantity);
    const unitPriceUsd = Number(row.unitPriceUsd);
    if (!row.description.trim()) continue;
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    if (!Number.isFinite(unitPriceUsd) || unitPriceUsd < 0) continue;
    out.push({ description: row.description.trim(), quantity, unit: row.unit, unitPriceUsd });
  }
  return out;
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function QuoteTemplatesPage(): React.ReactElement {
  useDocumentTitle('Quote templates');

  const [templates, setTemplates] = useState<QuoteTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Inactive ones included: a retired template still has to be
      // findable, or it can never be brought back.
      const data = await apiFetch<{ templates: QuoteTemplateSummary[] }>(
        '/api/quote-documents/templates?includeInactive=true',
      );
      setTemplates(data.templates);
    } catch (e) {
      setLoadError(
        e instanceof ApiError && e.status === 403
          ? 'Quote templates are limited to quote agents.'
          : 'Could not load templates.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function openEditor(id: string): Promise<void> {
    setSaveError(null);
    try {
      const data = await apiFetch<{ template: QuoteTemplate }>(
        `/api/quote-documents/templates/${id}`,
      );
      setDraft(draftFrom(data.template));
      setEditingId(id);
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : 'Could not open that template.');
    }
  }

  async function save(): Promise<void> {
    if (!draft) return;
    setBusy(true);
    setSaveError(null);
    const payload = {
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      intro: draft.intro.trim() || null,
      closing: draft.closing.trim() || null,
      sections: draft.sections
        .filter((s) => s.heading.trim())
        .map((s) => ({
          heading: s.heading.trim(),
          blocks: textToBlocks(s.text),
          collapsible: s.collapsible,
          showsPricing: s.showsPricing,
        })),
      planOptions: draft.planOptions,
      deliveryPlan: draft.deliveryPlan,
      lineItems: pricedRows(draft.lineItems),
      isActive: draft.isActive,
      position: draft.position,
      depositPercent: draft.depositPercent,
    };
    try {
      if (editingId) {
        await apiFetch(`/api/quote-documents/templates/${editingId}`, {
          method: 'PUT',
          json: payload,
        });
      } else {
        await apiFetch('/api/quote-documents/templates', { method: 'POST', json: payload });
      }
      setDraft(null);
      setEditingId(null);
      await load();
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : 'Could not save the template.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <Card>
        <h1 className="text-lg font-semibold text-[var(--color-header)]">Quote templates</h1>
        <p className="mt-2 text-sm text-[var(--color-muted)]">{loadError}</p>
      </Card>
    );
  }

  if (draft) {
    const totals = quoteTotals(pricedRows(draft.lineItems));
    return (
      <div className="space-y-6">
        <div>
          <button
            type="button"
            onClick={() => {
              setDraft(null);
              setEditingId(null);
              setSaveError(null);
            }}
            className="text-sm text-[var(--color-muted)] hover:text-[var(--color-header)] hover:underline"
          >
            ← All templates
          </button>
          <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">
            {editingId ? 'Edit template' : 'New template'}
          </h1>
          <p className="text-sm text-[var(--color-muted)]">
            Quotes already written from this template keep their own copy and are not changed by
            anything you do here.
          </p>
        </div>

        <Card>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Template name
              </span>
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className={inputClass}
                placeholder="Environment health review"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Shown under the name{' '}
                <span className="font-normal text-[var(--color-muted)]">(optional)</span>
              </span>
              <input
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                className={inputClass}
                placeholder="Time-boxed review across network, infrastructure and systems."
              />
            </label>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-[var(--color-body)]">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                className="h-4 w-4 accent-[var(--color-t3-blue)]"
              />
              Offered when starting a quote
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Order in the picker
              </span>
              <input
                type="number"
                min="0"
                max="1000"
                value={draft.position}
                onChange={(e) => setDraft({ ...draft, position: Number(e.target.value) || 0 })}
                className={inputClass}
              />
              <span className="mt-1 block text-xs text-[var(--color-muted)]">
                Lower comes first. Put what you sell most at the top.
              </span>
            </label>
            {/* On the template because it is a property of what is sold,
                not of one quote: fixed-fee project work is 50/50, and
                block hours are invoiced monthly — asking half a month's
                fee to sign one would contradict the MSA the same quote
                summarises. It is editable here so this page cannot
                silently reset it on an unrelated copy edit. */}
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
                Deposit at signature
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={draft.depositPercent}
                  onChange={(e) =>
                    setDraft({ ...draft, depositPercent: Number(e.target.value) || 0 })
                  }
                  className={inputClass}
                />
                <span className="text-sm text-[var(--color-muted)]">%</span>
              </div>
              <span className="mt-1 block text-xs text-[var(--color-muted)]">
                {draft.depositPercent === 0
                  ? 'No card taken to sign — the work is invoiced.'
                  : `${draft.depositPercent}% is taken by card before the signature is recorded.`}
              </span>
            </label>
          </div>

          <label className="mt-4 block">
            <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
              Introduction <span className="font-normal text-[var(--color-muted)]">(optional)</span>
            </span>
            <textarea
              rows={3}
              value={draft.intro}
              onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
              className={inputClass}
            />
          </label>
        </Card>

        <Card>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--color-header)]">Sections</h2>
            <button
              type="button"
              onClick={() =>
                setDraft({
                  ...draft,
                  sections: [
                    ...draft.sections,
                    { heading: '', text: '', collapsible: false, showsPricing: false },
                  ],
                })
              }
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-t3-blue)] hover:underline"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add section
            </button>
          </div>
          <div className="space-y-3">
            {draft.sections.map((section, index) => (
              <div
                key={index}
                className="rounded-[var(--radius-button)] border border-[var(--color-border)] p-3"
              >
                <div className="flex items-center gap-2">
                  <input
                    value={section.heading}
                    onChange={(e) => {
                      const sections = [...draft.sections];
                      sections[index] = { ...section, heading: e.target.value };
                      setDraft({ ...draft, sections });
                    }}
                    className={`${inputClass} font-semibold`}
                    placeholder="Section heading"
                    aria-label={`Section ${index + 1} heading`}
                  />
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, sections: move(draft.sections, index, index - 1) })}
                    disabled={index === 0}
                    className="rounded p-1.5 text-[var(--color-muted)] hover:bg-[var(--color-surface)] disabled:opacity-30"
                    aria-label={`Move section ${index + 1} up`}
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, sections: move(draft.sections, index, index + 1) })}
                    disabled={index === draft.sections.length - 1}
                    className="rounded p-1.5 text-[var(--color-muted)] hover:bg-[var(--color-surface)] disabled:opacity-30"
                    aria-label={`Move section ${index + 1} down`}
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-[var(--color-muted)]">
                    <input
                      type="checkbox"
                      checked={section.collapsible}
                      onChange={(e) => {
                        const sections = [...draft.sections];
                        sections[index] = {
                          ...section,
                          collapsible: e.target.checked,
                          showsPricing: e.target.checked ? false : section.showsPricing,
                        };
                        setDraft({ ...draft, sections });
                      }}
                      className="h-4 w-4 rounded border-[var(--color-border)]"
                    />
                    Rolled up
                  </label>
                  <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-[var(--color-muted)]">
                    <input
                      type="checkbox"
                      checked={section.showsPricing}
                      disabled={section.collapsible}
                      onChange={(e) => {
                        const sections = draft.sections.map((s, i) => ({
                          ...s,
                          showsPricing: i === index ? e.target.checked : false,
                        }));
                        setDraft({ ...draft, sections });
                      }}
                      className="h-4 w-4 rounded border-[var(--color-border)]"
                    />
                    Pricing here
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({ ...draft, sections: draft.sections.filter((_, i) => i !== index) })
                    }
                    className="rounded p-1.5 text-[var(--color-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]"
                    aria-label={`Remove section ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <div className="mt-2">
                  <textarea
                    rows={Math.min(24, Math.max(6, section.text.split('\n').length + 2))}
                    value={section.text}
                    onChange={(e) => {
                      const sections = [...draft.sections];
                      sections[index] = { ...section, text: e.target.value };
                      setDraft({ ...draft, sections });
                    }}
                    className={inputClass}
                    placeholder={
                      'What this section says.\n\n- A bullet\n- Another bullet\n\n1. A numbered step\n\n## A subheading'
                    }
                    aria-label={`Section ${index + 1} text`}
                  />
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    Start a line with <code>-</code> for a bullet, <code>1.</code> for a numbered
                    step, <code>##</code> for a subheading. Indent three spaces to nest a list
                    item. Wrap words in <code>**</code> for bold. A line starting with{' '}
                    <code>\</code> stays plain text. <code>[CLIENT NAME]</code> and{' '}
                    <code>[CLIENT ADDRESS]</code> are filled from the client record when a quote
                    is sent.
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* A plan template prices itself from the client's choice. Shown
            whenever the template has plans, so editing one can never
            post it back without them. */}
        {/* How this kind of work is delivered: copied into each quote
            started from the template, and from there into the project. */}
        {draft.planOptions.length === 0 && (
          <Card>
            <DeliveryPlanEditor
              plan={draft.deliveryPlan}
              onChange={(deliveryPlan) => setDraft({ ...draft, deliveryPlan })}
            />
          </Card>
        )}

        {draft.planOptions.length > 0 && (
          <Card>
            <PlanEditor
              plans={draft.planOptions}
              readOnly={false}
              onChange={(planOptions) => setDraft({ ...draft, planOptions })}
            />
          </Card>
        )}

        <Card>
          <div className="mb-2 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-header)]">
                Default pricing
              </h2>
              <p className="text-sm text-[var(--color-muted)]">
                A starting point that whoever quotes is expected to change. Leave it empty if this
                engagement is priced from scratch every time.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setDraft({
                  ...draft,
                  lineItems: [
                    ...draft.lineItems,
                    { description: '', quantity: '', unit: 'hours', unitPriceUsd: '' },
                  ],
                })
              }
              className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[var(--color-t3-blue)] hover:underline"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add line
            </button>
          </div>

          <datalist id="quote-units">
            {QUOTE_UNIT_SUGGESTIONS.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>

          {draft.lineItems.length > 0 && (
            <div className="overflow-x-auto rounded-[var(--radius-button)] border border-[var(--color-border)]">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                    <th className="px-3 py-2 font-semibold">Description</th>
                    <th className="w-24 px-3 py-2 font-semibold">Qty</th>
                    <th className="w-28 px-3 py-2 font-semibold">Unit</th>
                    <th className="w-32 px-3 py-2 font-semibold">Unit price</th>
                    <th className="w-28 px-3 py-2 text-right font-semibold">Amount</th>
                    <th className="w-10 px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {draft.lineItems.map((item, index) => {
                    const quantity = Number(item.quantity);
                    const price = Number(item.unitPriceUsd);
                    const complete =
                      Number.isFinite(quantity) && quantity > 0 && Number.isFinite(price) && price >= 0;
                    return (
                      <tr key={index} className="border-b border-[var(--color-border)] last:border-0">
                        <td className="px-3 py-2">
                          <input
                            value={item.description}
                            onChange={(e) => {
                              const lineItems = [...draft.lineItems];
                              lineItems[index] = { ...item, description: e.target.value };
                              setDraft({ ...draft, lineItems });
                            }}
                            className={inputClass}
                            aria-label={`Line ${index + 1} description`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.25"
                            value={item.quantity}
                            onChange={(e) => {
                              const lineItems = [...draft.lineItems];
                              lineItems[index] = { ...item, quantity: e.target.value };
                              setDraft({ ...draft, lineItems });
                            }}
                            className={inputClass}
                            aria-label={`Line ${index + 1} quantity`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            list="quote-units"
                            value={item.unit}
                            onChange={(e) => {
                              const lineItems = [...draft.lineItems];
                              lineItems[index] = { ...item, unit: e.target.value };
                              setDraft({ ...draft, lineItems });
                            }}
                            className={inputClass}
                            placeholder="hours"
                            aria-label={`Line ${index + 1} unit`}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitPriceUsd}
                            onChange={(e) => {
                              const lineItems = [...draft.lineItems];
                              lineItems[index] = { ...item, unitPriceUsd: e.target.value };
                              setDraft({ ...draft, lineItems });
                            }}
                            className={inputClass}
                            aria-label={`Line ${index + 1} unit price`}
                          />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-[var(--color-body)]">
                          {complete ? money(lineItemAmount({ quantity, unitPriceUsd: price })) : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() =>
                              setDraft({
                                ...draft,
                                lineItems: draft.lineItems.filter((_, i) => i !== index),
                              })
                            }
                            className="rounded p-1.5 text-[var(--color-muted)] hover:bg-rose-50 hover:text-[var(--color-danger)]"
                            aria-label={`Remove line ${index + 1}`}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[var(--color-surface)] font-semibold text-[var(--color-header)]">
                    <td className="px-3 py-2" colSpan={2}>
                      Default total
                    </td>
                    <td className="px-3 py-2 tabular-nums" colSpan={2}>
                      {totals.hours} h
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(totals.amountUsd)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>

        <Card>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[var(--color-header)]">
              Terms / next steps{' '}
              <span className="font-normal text-[var(--color-muted)]">(optional)</span>
            </span>
            <textarea
              rows={3}
              value={draft.closing}
              onChange={(e) => setDraft({ ...draft, closing: e.target.value })}
              className={inputClass}
            />
          </label>
        </Card>

        {saveError && <p className="text-sm text-[var(--color-danger)]">{saveError}</p>}

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void save()} disabled={busy || !draft.name.trim()}>
            {busy ? 'Saving…' : 'Save template'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setDraft(null);
              setEditingId(null);
              setSaveError(null);
            }}
            disabled={busy}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/admin/quotes"
            className="text-sm text-[var(--color-muted)] hover:text-[var(--color-header)] hover:underline"
          >
            ← Quotes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">Quote templates</h1>
          <p className="text-sm text-[var(--color-muted)]">
            The engagements we sell often. Applying one copies it into the new quote.
          </p>
        </div>
        <Button onClick={() => setDraft(emptyDraft())}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          New template
        </Button>
      </div>


      {saveError && <p className="text-sm text-[var(--color-danger)]">{saveError}</p>}

      {loading ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">Loading templates…</p>
        </Card>
      ) : templates.length === 0 ? (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            No templates yet. Add the engagements you quote most often and they will appear when
            starting a quote.
          </p>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <th className="px-3 py-2.5 font-semibold">Template</th>
                <th className="px-3 py-2.5 font-semibold">Sections</th>
                <th className="px-3 py-2.5 text-right font-semibold">Default hours</th>
                <th className="px-3 py-2.5 text-right font-semibold">Default value</th>
                <th className="px-3 py-2.5 font-semibold">Offered</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id} className="border-b border-[var(--color-border)] last:border-0">
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => void openEditor(t.id)}
                      className="font-medium text-[var(--color-t3-blue)] hover:underline"
                    >
                      {t.name}
                    </button>
                    {t.description && (
                      <span className="mt-0.5 block text-xs text-[var(--color-muted)]">
                        {t.description}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-[var(--color-body)]">
                    {t.sectionCount}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[var(--color-body)]">
                    {t.lineItemCount === 0 ? '—' : t.totalHours}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[var(--color-body)]">
                    {t.lineItemCount === 0 ? '—' : money(t.totalAmountUsd)}
                  </td>
                  <td className="px-3 py-2.5">
                    {t.isActive ? (
                      <span className="text-[var(--color-body)]">Yes</span>
                    ) : (
                      <span className="text-[var(--color-muted)]">Retired</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => void openEditor(t.id)}
                      className="text-sm text-[var(--color-t3-blue)] hover:underline"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
